/**
 * idleTrackerService
 * - updates sessionStorage 'lastActivity' on user events
 * - exposes:
 *    startActivityTimestampLogger(): () => void
 *    startIdleMonitor(timeoutMs: number, onTimeout: () => void): () => void
 *
 * Minimal console output: only timestamps / simple idle log.
 */

const EVENT_TYPES = [
  "click",
  "dblclick",
  "mousedown",
  "mouseup",
  "mousemove",
  "contextmenu",
  "wheel",
  "pointerdown",
  "pointerup",
  "pointermove",
  "keydown",
  "keyup",
  "keypress",
] as const;

let loggerRunning = false;
let loggerHandler: (e: Event) => void;
let visibilityHandler: () => void;
let monitorIntervalId: ReturnType<typeof globalThis.setInterval> | null = null;

/**
 * Start logging activity timestamps to sessionStorage and console.
 * Returns a stop function that removes listeners.
 */
export function startActivityTimestampLogger(): () => void {
  if (loggerRunning) {
    return () => {};
  }

  // Seed lastActivity immediately so idle monitor has a baseline
  // even if the user never interacts after login.
  try {
    sessionStorage.setItem("lastActivity", String(Date.now()));
  } catch {
    // ignore storage errors
  }

  // Log once that activity tracking has started
  // eslint-disable-next-line no-console
  console.log("[activity-ts] activity tracking started");

  loggerHandler = (_e: Event) => {
    const epoch = Date.now();
    try {
      sessionStorage.setItem("lastActivity", String(epoch));
    } catch {
      // ignore storage errors
    }
    //const iso = new Date(epoch).toISOString();
    // minimal console output
    // eslint-disable-next-line no-console
    //console.log('[activity-ts]', epoch, iso);
  };

  for (const ev of EVENT_TYPES) {
    globalThis.addEventListener(ev, loggerHandler, { passive: true });
  }

  visibilityHandler = () => {
    const epoch = Date.now();
    try {
      sessionStorage.setItem("lastActivity", String(epoch));
    } catch {}
    //const iso = new Date(epoch).toISOString();
    // eslint-disable-next-line no-console
    //console.log('[activity-ts][visibilitychange]', document.hidden ? 'hidden' : 'visible', epoch, iso);
  };
  globalThis.addEventListener("visibilitychange", visibilityHandler);

  loggerRunning = true;

  return () => {
    if (!loggerRunning) return;
    for (const ev of EVENT_TYPES) {
      globalThis.removeEventListener(ev, loggerHandler as EventListener);
    }
    globalThis.removeEventListener("visibilitychange", visibilityHandler);
    loggerRunning = false;
  };
}

/**
 * Start an idle monitor that checks the lastActivity timestamp and
 * calls onTimeout() when inactivity > timeoutMs.
 * Returns a stop() function to cancel monitoring.
 */
export function startIdleMonitor(
  timeoutMs: number,
  onTimeout: () => void,
): () => void {
  // Ensure logger is running so lastActivity keeps updating.
  const stopLogger = startActivityTimestampLogger();

  // Determine polling interval (not too frequent)
  const pollInterval = Math.max(
    1000,
    Math.min(10000, Math.floor(timeoutMs / 6)),
  );

  // Clear previous interval if any
  if (monitorIntervalId !== null) {
    clearInterval(monitorIntervalId);
    monitorIntervalId = null;
  }

  // Record the time monitoring started — used as fallback if lastActivity
  // is missing (e.g. user never interacted after login, or sessionStorage was cleared).
  const monitorStartTime = Date.now();

  // Track whether onTimeout has already been called to prevent duplicate invocations
  let firedAlready = false;

  monitorIntervalId = globalThis.setInterval(() => {
    if (firedAlready) return;

    try {
      const lastStr = sessionStorage.getItem("lastActivity");
      // If lastActivity was never written (null/empty), fall back to monitorStartTime.
      // This ensures users who log in and do nothing are still logged out.
      const last = lastStr ? Number(lastStr) : monitorStartTime;
      const now = Date.now();

      if (last > 0 && now - last >= timeoutMs) {
        firedAlready = true;

        // eslint-disable-next-line no-console
        console.log("[idle-tracker] idle detected", {
          last,
          now,
          diffMs: now - last,
        });

        // Clear interval immediately to prevent duplicate onTimeout calls
        if (monitorIntervalId !== null) {
          clearInterval(monitorIntervalId);
          monitorIntervalId = null;
        }

        try {
          onTimeout();
        } catch (e) {
          console.error("onTimeout error", e);
        }
      }
    } catch (e) {
      console.error("idleMonitor read error", e);
    }
  }, pollInterval);

  return () => {
    if (monitorIntervalId !== null) {
      clearInterval(monitorIntervalId);
      monitorIntervalId = null;
    }
    try {
      stopLogger();
    } catch {}
  };
}

export default startActivityTimestampLogger;
